'use client';

import { useEffect, useMemo, useState } from 'react';

import { FlexBox, Justify, Spacer, SpacerSize, TitleMarkup } from '@bytel/trilogy-react';

import { ShortListContext } from '@app-types/eligibility';
import { FaqConfig } from '@app-types/eligibility/config';
import { FaiTabsEnum, TabConfig } from '@app-types/home-fai';
import { OffersByCategoryCode } from '@app-types/plan';

import { FaiOffersByTabs } from '@components/fai-offers-by-tabs';
import { Faq } from '@components/faq';
import { SSIBlock } from '@components/ssi-block';

import { partials } from '@helpers/partials';

import { ELIGIBILITY_JOURNEY, TECHNOLOGY } from '@constants/provider';

import { EligibilityResults, JourneyConfigType, normalizeTechnology } from '@eligibility/helpers';

import { Forecast } from '../forecast';
import { OfferList } from './offer-list';

const tabsOrder: string[] = [TECHNOLOGY.FTTH, TECHNOLOGY.BOX_5G, TECHNOLOGY.BOX_4G];

function getXGBoxLabel(results: EligibilityResults): string {
    let label = '4G box';
    const offers = results?.offers?.BOX_4G;
    if (!!offers) {
        const has4GBox = offers.some((o) => o.technology === TECHNOLOGY.BOX_4G);
        const has5GBox = offers.some((o) => o.technology === TECHNOLOGY.BOX_5G);
        if (has5GBox) {
            label = '5G box';
            if (has4GBox) {
                label = '4G / 5G box';
            }
        }
    }
    return label;
}

function getTechnologyName(technology: TECHNOLOGY, results: EligibilityResults): string | undefined {
    const technologyName: { [key in TECHNOLOGY]?: string } = {
        [TECHNOLOGY.FTTH]: 'Fibre',
        [TECHNOLOGY.BOX_4G]: getXGBoxLabel(results),
        [TECHNOLOGY.BOX_5G]: getXGBoxLabel(results),
    };

    return technologyName[technology];
}

interface EligibilityResultTabsProps {
    results: EligibilityResults;
    journeyConfig: JourneyConfigType;
    shortList?: ShortListContext;
    offersByCategory: OffersByCategoryCode;
    onAddToCart: (gencode: string) => void;
    tabConfig: TabConfig;
    defaultFaiTab: FaiTabsEnum;
    onTabChange: () => void;
}

export function EligibilityResultTabs({
    results,
    journeyConfig,
    shortList,
    offersByCategory,
    onAddToCart,
    tabConfig,
    defaultFaiTab,
    onTabChange,
}: Readonly<EligibilityResultTabsProps>) {
    const [activeTab, setActiveTab] = useState<FaiTabsEnum>(defaultFaiTab);

    useEffect(() => {
        const hashTab = window.location.hash.replace('#', '').toLowerCase() as FaiTabsEnum;
        setActiveTab(tabConfig[hashTab] ? hashTab : defaultFaiTab);
    }, [defaultFaiTab, tabConfig]);

    const isPackAvailable = useMemo(
        () => journeyConfig.showPackTabs && !!results.offers?.FTTH,
        [journeyConfig, results],
    );

    const [defaultTab] = useMemo(() => {
        const technologies = Object.keys(results.offers)
            .map((technology) => {
                return { name: technology, label: getTechnologyName(technology as TECHNOLOGY, results)! };
            })
            .filter((technology) => !!technology.label)
            .sort((a, b) => tabsOrder.indexOf(a.name) - tabsOrder.indexOf(b.name));

        if (isPackAvailable) {
            technologies.push({ name: 'PACK', label: 'Pack' });
        }

        const shortListTechnology = normalizeTechnology(shortList?.technology);

        const shortListTab = technologies.find((t) => t.name === shortListTechnology);

        const defaultTab = shortListTab ?? technologies.find((t) => t.name === results.technology) ?? technologies[0];

        if (shortListTab && shortList) {
            defaultTab!.name = shortList.technology.toUpperCase() as TECHNOLOGY;
        }

        return [defaultTab];
    }, [results, journeyConfig, shortList, isPackAvailable]);

    const hasFallbackOffers = useMemo(
        () => Object.values(results.offers).some((offers) => offers.some((offer) => offer.isFallbackMismatchOffer)),
        [results.offers],
    );

    const faqConfig: FaqConfig = useMemo(() => {
        if (journeyConfig.name === ELIGIBILITY_JOURNEY.LM) {
            const lmPartner = results.lmPartner;
            return {
                title: 'Vos questions fréquentes sur l’éligibilité à la fibre',
                path: `offres-internet_${lmPartner?.value}`,
            } as FaqConfig;
        } else {
            return journeyConfig.faq;
        }
    }, [journeyConfig, results.lmPartner]);

    const plans = Object.fromEntries(
        Object.values(results.offers)
            .flatMap((offers) => offers.filter((o) => !o.isHidden))
            .map((o) => [
                `${o.rangeNg}_${o.commercialSeries}-${o.technology}`,
                { ...o.mapToFaiType(), isEligible: true },
            ]),
    );

    const handleTabChange = (tab: FaiTabsEnum) => {
        setActiveTab(tab);
        window.location.hash = tab;
        onTabChange();
    };

    return (
        <>
            {shortList?.focus ? (
                <OfferList
                    currentTab={defaultTab as { name: TECHNOLOGY; label: string }}
                    journeyConfig={journeyConfig}
                    results={results}
                    shortList={shortList}
                    categoryCodeInfo={offersByCategory[journeyConfig.offerCategoryCodeToShow]!}
                    onAddToCart={onAddToCart}
                />
            ) : (
                <FaiOffersByTabs
                    tabConfig={tabConfig}
                    defaultTab={activeTab}
                    offersByCategory={offersByCategory}
                    categoriesToShow={[journeyConfig.offerCategoryCodeToShow]}
                    plans={plans}
                    onAddToCart={onAddToCart}
                    showComparatorSelection={journeyConfig.showComparatorSelection}
                    showDetailsButton={journeyConfig.showDetailsButton}
                    setActiveTab={handleTabChange}
                    extraCmsBlockByTab={journeyConfig.extraCmsBlockByTab}
                />
            )}
            {defaultTab?.name === TECHNOLOGY.FTTH && <SSIBlock url={partials.MARKETING.ELIGIBILITY_FTTH} />}
            <Spacer size={SpacerSize.TEN} />
            {!hasFallbackOffers && Object.values(results.offers).length > 0 && (
                <FlexBox justify={Justify.CENTER}>
                    <Forecast results={results} journeyConfig={journeyConfig} />
                </FlexBox>
            )}
            <Spacer size={SpacerSize.TEN} />
            <Faq titleMarkup={TitleMarkup.H3} title={faqConfig.title} url={faqConfig.path} />
        </>
    );
}
